import React, { useState, useRef, useEffect } from 'react';

// Typeahead replacement for the native <select> when picking a supplier from
// a long list. Shows the current selection in the field; focus / typing opens
// the dropdown and filters by name.
//
// Kept supplier-specific for now since that's the only use case — once a
// second consumer appears, generalise into a Combobox that takes
// `options`, `getKey`, `getLabel`.
export default function SupplierCombobox({ value, onChange, suppliers, placeholder = 'Type to search suppliers...', inputClass = '' }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);

  const selected = (suppliers || []).find(s => s.id === value);
  // When closed, render the selected supplier's name in the input. When open,
  // render the typed query so the user sees what they're filtering by.
  const display = open ? query : (selected?.supplier_name || '');

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = (suppliers || []).filter(s =>
    (s.supplier_name || '').toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="relative" ref={wrapRef}>
      <input
        type="text"
        className={inputClass}
        value={display}
        placeholder={placeholder}
        onFocus={() => { setOpen(true); setQuery(''); }}
        onChange={e => { setQuery(e.target.value); if (!open) setOpen(true); }}
        onKeyDown={e => {
          if (e.key === 'Escape') { setOpen(false); setQuery(''); e.currentTarget.blur(); }
        }}
      />
      {open && (
        <div className="absolute z-10 left-0 right-0 mt-1 max-h-64 overflow-y-auto bg-white border border-line shadow-lg">
          {filtered.length === 0 ? (
            <p className="font-['Metropolis'] text-sm text-[#51534A] px-3 py-2">No matching suppliers.</p>
          ) : filtered.map(s => (
            <button
              key={s.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onChange(s.id); setOpen(false); setQuery(''); }}
              className={`block w-full text-left px-3 py-2 font-['Metropolis'] text-sm hover:bg-white transition-colors ${value === s.id ? 'text-fusion-forest' : 'text-ink'}`}>
              {s.supplier_name}
            </button>
          ))}
          {value && (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onChange(''); setOpen(false); setQuery(''); }}
              className="block w-full text-left px-3 py-2 font-['Metropolis'] text-xs tracking-[1px] uppercase text-[#51534A] hover:text-[#E0290A] border-t border-line transition-colors">
              Clear selection
            </button>
          )}
        </div>
      )}
    </div>
  );
}
